import {
  CheckCircle2,
  Clock,
  DollarSign,
  Calendar,
  Users,
  FileText,
  GitCommit,
  GitPullRequest,
  Rocket,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { ServiceOrderDto } from "@shared/types/ServiceOrderDto";
import { useEffect, useState } from "react";
import { useWorkOrders } from "@/providers/WorkOrdersProvider";
import { WorkOrderDto } from "@shared/types/WorkOrderDto";

interface OverviewSectionProps {
  serviceOrder: ServiceOrderDto;
}

export function OverviewSection({ serviceOrder }: OverviewSectionProps) {
  const { project, financials, payments, deposit } = serviceOrder;
  const { selectWorkOrder } = useWorkOrders();
  const [selectedWorkOrder, setSelectedWorkOrder] =
    useState<WorkOrderDto | null>(null);

  useEffect(() => {
    if (payments.length > 0) {
      selectWorkOrder(serviceOrder.id)
        .then((workOrder) => {
          setSelectedWorkOrder(workOrder);
        })
        .catch((err) => {
          console.error("Error selecting work order:", err);
        });
    }
  }, [serviceOrder.id, payments, selectWorkOrder]);

  const totalPaid = payments
    .filter((p) => p.status === "paid")
    .reduce((sum, p) => sum + p.amount, 0);
  const depositPaid = deposit.status === "paid" ? deposit.amount : 0;
  const totalSpend = totalPaid + depositPaid;
  const budgetPercent = Math.round((totalSpend / financials.budget) * 100);

  const completedMilestones = project.milestones.filter(
    (m) => m.developmentStatus === "completed"
  ).length;

  const recentCommits = selectedWorkOrder?.workUnits.flatMap((wu) => wu.commits).slice(0, 5) || [];
  const recentDeployments =
    selectedWorkOrder?.workUnits.flatMap((wu) => wu.deployments).slice(0, 5) || [];
  const recentPRs = selectedWorkOrder?.workUnits.flatMap((wu) => wu.pullRequests).slice(0, 5) || [];

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Spent / Budget</p>
                <p className="font-semibold">
                  ${totalSpend.toLocaleString()} / $
                  {financials.budget.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${Math.min(budgetPercent, 100)}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-400/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Milestones</p>
                <p className="font-semibold">
                  {completedMilestones} / {project.milestones.length} Complete
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-400/10 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Due Date</p>
                <p className="font-semibold">
                  {new Date(serviceOrder.dueDate).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-400/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Started</p>
                <p className="font-semibold">
                  {new Date(serviceOrder.startDate).toLocaleDateString(
                    "en-US",
                    {
                      month: "long",
                      day: "numeric",
                    }
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card border-gradient">
        <CardHeader>
          <CardTitle className="text-lg">Project Scope</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">{project.scope.description}</p>

          <div>
            <p className="text-sm font-medium mb-2">Inclusions</p>
            <div className="flex flex-wrap gap-2">
              {project.scope.inclusions.map((item, i) => (
                <Badge key={i} variant="secondary">
                  {item}
                </Badge>
              ))}
            </div>
          </div>

          {project.scope.exclusions.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Exclusions</p>
              <div className="flex flex-wrap gap-2">
                {project.scope.exclusions.map((item, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="text-muted-foreground"
                  >
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedWorkOrder && (
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="glass-card border-gradient">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <GitCommit className="h-5 w-5" />
                Recent Commits
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentCommits.length > 0 ? (
                <div className="space-y-2">
                  {recentCommits.map((commit, i) => (
                    <a
                      key={i}
                      href={commit.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-2 rounded-lg bg-card/30 hover:bg-card/50 transition-colors border border-border/20"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">
                            {commit.commitMessage}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {commit.branch} · {commit.shasum.slice(0, 7)}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground mt-1 block">
                        {new Date(commit.createdAt).toLocaleDateString()}
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No commits yet</p>
              )}
            </CardContent>
          </Card>

          <Card className="glass-card border-gradient">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Rocket className="h-5 w-5" />
                Recent Deployments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentDeployments.length > 0 ? (
                <div className="space-y-2">
                  {recentDeployments.map((deployment, i) => (
                    <a
                      key={i}
                      href={deployment.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-2 rounded-lg bg-card/30 hover:bg-card/50 transition-colors border border-border/20"
                    >
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-medium truncate flex-1">
                          {deployment.name}
                        </p>
                        {deployment.state === "PASSED" && (
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                        )}
                        {deployment.state === "FAILED" && (
                          <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                        )}
                        {deployment.state === "BUILDING" && (
                          <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {deployment.branch} · {deployment.state}
                      </p>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No deployments yet
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="glass-card border-gradient">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <GitPullRequest className="h-5 w-5" />
                Recent Pull Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentPRs.length > 0 ? (
                <div className="space-y-2">
                  {recentPRs.map((pr, i) => (
                    <a
                      key={i}
                      href={pr.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-2 rounded-lg bg-card/30 hover:bg-card/50 transition-colors border border-border/20"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs font-medium truncate flex-1">
                          {pr.title}
                        </p>
                        {pr.mergeStatus === "MERGED" && (
                          <CheckCircle className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                        )}
                        {pr.mergeStatus === "CLOSED" && (
                          <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                        )}
                        {pr.mergeStatus === "OPEN" && (
                          <AlertCircle className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {pr.originBranch} → {pr.targetBranch}
                      </p>
                      <span className="text-xs text-muted-foreground mt-1 block">
                        {new Date(pr.createdAt).toLocaleDateString()}
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No pull requests yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="glass-card border-gradient">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Deliverables
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {project.deliverables.map((d) => (
                <div
                  key={d.id}
                  className="p-3 rounded-lg bg-card/50 border border-border/30"
                >
                  <p className="font-medium text-sm">{d.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {d.description}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Due: {new Date(d.dueDate).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-gradient">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Project Team
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-2 rounded bg-card/50">
                <span className="text-sm text-muted-foreground">
                  Project Manager
                </span>
                <span className="font-medium text-sm">
                  {project.team.projectManager}
                </span>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-card/50">
                <span className="text-sm text-muted-foreground">Tech Lead</span>
                <span className="font-medium text-sm">
                  {project.team.techLead}
                </span>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-card/50">
                <span className="text-sm text-muted-foreground">
                  QA Engineer
                </span>
                <span className="font-medium text-sm">
                  {project.team.qaEngineer}
                </span>
              </div>
              <div className="p-2 rounded bg-card/50">
                <span className="text-sm text-muted-foreground">
                  Developers
                </span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {project.team.developers.map((dev, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {dev}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card border-gradient">
        <CardHeader>
          <CardTitle className="text-lg">Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Start Date</p>
              <p className="font-medium">
                {new Date(project.timeline.startDate).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">End Date</p>
              <p className="font-medium">
                {new Date(project.timeline.endDate).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Working Hours</p>
              <p className="font-medium text-sm">
                {project.timeline.workingHours}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Holidays</p>
              <p className="font-medium text-sm">{project.timeline.holidays}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
